import { useEffect, useMemo, useRef, useState } from 'react';
import '/assets/styles/sections/order-lookup.scss';

// Partials
import Btn from '/views/partials/btn';
import { FormFeedback } from '/views/partials/forms';
import Loader from '/views/partials/loader';
import LotteryHeadline from '/views/partials/lottery-headline';

// Context
import { useAuth } from '@wearetla/tla-essentials-tools/utilities/auth';
import { useLocation } from 'react-router-dom';
import { useLottery, parseLotteryQrParams } from '/utilities/lottery';
import { useTranslation } from '/utilities/i18n';

// Services
import orderServices from '/services/order';
import classNames from 'classnames';

// Dikkat: sipariş sorgulama tokenı BelgeID-BrID sırasındadır; çekilişteki `relation_id` ise BrID-BelgeID.
const getToken = (search) => {
	const params = new URLSearchParams(search ?? '');
	const brId = params.get('BrID');
	const belgeId = params.get('BelgeID');

	return brId && belgeId ? `${belgeId}-${brId}` : false;
};

const OrderLookup = () => {
	const { search } = useLocation();
	const { t } = useTranslation();

	// SSR sırasında sorgu parametreleri okunamadığı (ve önbelleğe alınamayacağı) için
	// sorguya bağlı içerik yalnızca istemcide render edilir.
	const [mounted, setMounted] = useState(false);
	const [feedbackData, setFeedbackData] = useState(null);
	const [orderData, setOrderData] = useState(null);

	const token = useMemo(() => (mounted ? getToken(search) : false), [mounted, search]);

	useEffect(() => {
		setMounted(true);
	}, []);

	useEffect(() => {
		if(!mounted) {
			return;
		}

		if(token) {
			setOrderData(null);
			orderServices.checkGuestOrder(token).then(payload => {
				setOrderData(payload);
			}).catch((e) => {
				setFeedbackData(e);
			})
		}
		else {
			setFeedbackData(t('Sipariş bilgisine ulaşılamadı, lütfen QR kodu tekrar okut.'));
		}
	}, [mounted, token])

	return (
		<section className="section order-lookup">
			<div className="wrapper">
				{mounted && <LotteryBanner search={search} />}
				<div className="lookup-orders loader-container">
					<Loader dark loading={!feedbackData && !orderData} />
					{feedbackData &&
						<FormFeedback className="lookup-feedback" data={feedbackData} />
					}
					{orderData &&
						<ul className="lookup-fragments">
							{orderData.map((fragment, nth) => (
								<li className="fragments-fragment" key={`${fragment.id}-${nth}`}>
									<div className="fragment-header">
										<div>
											<strong className="fragment-title">{t('Sipariş Numarası:')} {fragment.orderId}</strong>
											<span className="fragment-datetime">{fragment.dateTime}</span>
											<span className="fragment-location">{fragment.location}</span>
										</div>
										<OrderStatus className="fragment-status" status={fragment.status} />
									</div>

									<ul className="fragment-products">
										{fragment.products.map(product => (
											<li
												className="fragment-product"
												key={product.id}>
												<strong className="product-title">{product.title}</strong>
												<span className="product-quantity">
													{product.quantity} {product.unit}
												</span>
											</li>
										))}
									</ul>
								</li>
							))}
						</ul>
					}
				</div>
			</div>
		</section>
	);
};

const LotteryBanner = ({ search }) => {
	const { loggedIn, initialized: authInitialized } = useAuth();
	const {
		enabled,
		initialized: lotteryInitialized,
		userLotteryStatus,
		lotteries,
		getLottery,
		submitOrder,
	} = useLottery();
	const { t } = useTranslation();

	const submittedRef = useRef(false);
	const [submitting, setSubmitting] = useState(false);
	const [justEarned, setJustEarned] = useState(false);
	const [feedbackData, setFeedbackData] = useState(null);

	const qrParams = useMemo(() => parseLotteryQrParams(search), [search]);

	// Tanımlanmış hak, siparişin `relation_id` değeriyle eşleşir: BrID-BelgeID.
	// Fiş numarası (BelgeID) tek başına benzersiz olduğu için eşleşme onun üzerinden yapılır.
	const earnedItem = useMemo(() => {
		const belgeId = qrParams?.BelgeID?.toLowerCase();

		if (!belgeId) {
			return null;
		}

		return lotteries.find((item) => `${item.relation_id ?? ''}`.toLowerCase().includes(belgeId)) ?? null;
	}, [lotteries, qrParams]);

	const canSubmit = enabled && loggedIn && lotteryInitialized && userLotteryStatus && !!qrParams;

	useEffect(() => {
		if (!canSubmit || earnedItem || submittedRef.current) {
			return;
		}

		submittedRef.current = true;
		setSubmitting(true);
		setFeedbackData(null);

		submitOrder(qrParams)
			.then(() => {
				setJustEarned(true);
			})
			.catch((feedback) => {
				setFeedbackData(feedback);

				// Hak daha önce tanımlanmış olabilir; güncel listede varsa başarı durumu gösterilir.
				getLottery().catch(() => {});
			})
			.finally(() => {
				setSubmitting(false);
			});
	}, [canSubmit, earnedItem, qrParams, getLottery, submitOrder]);

	if (!enabled || !qrParams) {
		return null;
	}

	const loading = !authInitialized || (loggedIn && !lotteryInitialized) || submitting;

	return (
		<div className="lookup-lottery">
			<div className="lottery-content">
				{loading ? (
					<p className="lottery-message">{t('Çekiliş hakların kontrol ediliyor...')}</p>
				) : earnedItem ? (
					// Hak tanımlıysa, tanımlama isteği hata dönmüş olsa da başarı durumu gösterilir.
					<>
						<LotteryHeadline className="lottery-headline" />
						<p className="lottery-message">
							{justEarned
								? t('Bu siparişten {{total}} çekiliş hakkı kazandın.', {
										total: earnedItem.lottery_count,
									})
								: t('Bu siparişten daha önce {{total}} çekiliş hakkı kazanmıştın.', {
										total: earnedItem.lottery_count,
									})}
						</p>
						<Btn tag="link" href="account.lottery" className="lottery-btn small yellow-light">
							{t('Çekiliş Haklarım')}
						</Btn>
					</>
				) : feedbackData ? (
					<FormFeedback className="lottery-feedback" data={feedbackData} />
				) : (
					<>
						<LotteryHeadline className="lottery-headline" />
						<p className="lottery-message">
							{t('Bu siparişin ile çekiliş hakkı kazanabilirsin.')}
						</p>
						<Btn
							tag="link"
							href={loggedIn ? 'account.lottery' : 'login'}
							className="lottery-btn small yellow-light"
						>
							{loggedIn ? t('Kampanyaya Katıl') : t('Giriş Yap')}
						</Btn>
					</>
				)}
			</div>
		</div>
	);
};

const OrderStatus = ({ status, className }) => {
	const { t } = useTranslation();

	return (
		<span className={classNames('orderstatus', `status-${status}`, className)}>
			{status === 1 &&
				t('Hazır')
			}
			{status === 0 &&
				t('Hazırlanıyor')
			}
		</span>
	)
}

export default OrderLookup;
